// ============================================================
// SlashBar
// ============================================================

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { v4 as uuid } from 'uuid';
import type { Bar, Section, Chord, AnalysisSettings, RhythmSlot } from '../../core/types';
import {
  useSongStore,
  effectiveKey,
  effectiveTimeSignature,
} from '../../store/songStore';
import { ChordSymbol } from './ChordSymbol';
import { ChordAnalysisLabel } from './ChordAnalysisLabel';
import { formatNashville } from '../../core/formatter';
import {
  RepeatStartBarline,
  RepeatEndBarline,
  RepeatCountBadge,
  RepeatEndPopover,
} from './RepeatBarlines';
import { BarTimeSigControl } from './BarTimeSigControl';
import { AnnotationField } from './AnnotationField';
import { BarOptionsButton } from './BarOptionsButton';
import { LyricsField } from './LyricsField';
import { RhythmInputPanel, NoteIcon } from '../editor/RhythmInputPanel';
import { slotsToPositions } from '../editor/rhythmUtils';
import { useIsBarPlaying, usePlaybackCursor } from '../editor/PlaybackContext';
import type { NoteValue } from '../../core/types';

// ------------------------------------------------------------
// FONT SCALING
// ------------------------------------------------------------

const SCALE_FACTOR = 0.38;
const MIN_FONT_PX  = 13;
const MAX_FONT_PX  = 42;
const SLOT_PADDING = 8;

// ------------------------------------------------------------
// CLOSEST NOTE VALUE
// ------------------------------------------------------------

function closestNoteValue(beats: number): NoteValue {
  const table: Array<[number, NoteValue]> = [
    [4,    'whole'],    [3,    'dottedHalf'],   [2,    'half'],
    [1.5,  'dottedQuarter'], [1, 'quarter'],    [2/3,  'tripletQuarter'],
    [0.75, 'dottedEighth'],  [0.5, 'eighth'],   [1/3,  'tripletEighth'],
    [0.25, 'sixteenth'],
  ];
  let best = table[4][1]; let bestDist = Infinity;
  for (const [val, nv] of table) {
    const dist = Math.abs(beats - val);
    if (dist < bestDist) { bestDist = dist; best = nv; }
  }
  return best;
}

function chordsToRhythmSlots(bar: Bar, totalBeats: number): RhythmSlot[] {
  if (bar.chords.length === 0) return [];
  return bar.chords.map((chord, i) => {
    const nextPos = bar.chords[i + 1]?.position ?? totalBeats;
    const beats   = nextPos - chord.position;
    return { id: uuid(), noteValue: closestNoteValue(beats), beats, position: chord.position };
  });
}

// ------------------------------------------------------------
// SLASH MARKS
// ------------------------------------------------------------

function QuarterSlash() {
  return (
    <div style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
      <svg width="14" height="22" viewBox="0 0 14 22" style={{ display: 'block' }}>
        <line x1="11" y1="2" x2="3" y2="20" stroke="var(--color-ink-primary)" strokeWidth="1.8" strokeLinecap="round" opacity={0.65} />
      </svg>
    </div>
  );
}

function EighthSlash() {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 0 }}>
      <svg width="9" height="16" viewBox="0 0 9 16" style={{ display: 'block' }}>
        <line x1="7" y1="2" x2="2" y2="14" stroke="var(--color-ink-primary)" strokeWidth="1.2" strokeLinecap="round" opacity={0.32} />
      </svg>
    </div>
  );
}

// ------------------------------------------------------------
// CHORD → INPUT STRING
// ------------------------------------------------------------

function chordsToInputString(chords: Chord[], timeNumerator: number): string {
  if (chords.length === 0) return '';
  if (chords.length === 1) return formatNashville(chords[0]);
  const parts: string[] = [];
  for (let i = 0; i < chords.length; i++) {
    parts.push(formatNashville(chords[i]));
    if (i < chords.length - 1) {
      let remaining = chords[i + 1].position - chords[i].position;
      while (remaining > 0.001) {
        if      (remaining >= 1)   { parts.push('.');    remaining -= 1;    }
        else if (remaining >= 0.5) { parts.push('..');   remaining -= 0.5;  }
        else if (remaining >= 1/3) { parts.push('...');  remaining -= 1/3;  }
        else                       { parts.push('....'); remaining -= 0.25; }
      }
    }
  }
  return parts.join(' ');
}

// ------------------------------------------------------------
// useBarWidth
// ------------------------------------------------------------

function useBarWidth(ref: React.RefObject<HTMLDivElement>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(el);
    setWidth(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function computeFontSize(beatFraction: number, barWidthPx: number): number {
  if (barWidthPx === 0) return MAX_FONT_PX;
  const slotPx   = barWidthPx * beatFraction - SLOT_PADDING;
  const fontSize = slotPx * SCALE_FACTOR;
  return Math.min(MAX_FONT_PX, Math.max(MIN_FONT_PX, fontSize));
}

// ------------------------------------------------------------
// CHORD SEGMENT
// ------------------------------------------------------------

interface ChordSegmentProps {
  chord: Chord | null;
  beats: number;
  totalBeats: number;
  barWidthPx: number;
  eighthNotes: boolean;
  showNoteIcon: boolean;
  sectionKey: ReturnType<typeof effectiveKey>;
  displayMode: 'nashville' | 'roman' | 'chords';
  isLast: boolean;
  isChordPlaying: boolean;
  analysisSettings?: AnalysisSettings;
}

function ChordSegment({
  chord, beats, totalBeats, barWidthPx, eighthNotes,
  showNoteIcon, sectionKey, displayMode, isLast, isChordPlaying, analysisSettings,
}: ChordSegmentProps) {
  const slotsPerBeat = eighthNotes ? 3 : 2;
  const flexValue    = beats * slotsPerBeat;
  const beatFraction = totalBeats > 0 ? beats / totalBeats : 1;
  const fontSize     = computeFontSize(beatFraction, barWidthPx);
  const slotPx       = barWidthPx * beatFraction;
  const iconSize     = Math.min(32, Math.max(14, slotPx * 0.45));
  const noteValue    = showNoteIcon ? closestNoteValue(beats) : null;

  const slashes: Array<'quarter' | 'eighth'> = [];
  if (!showNoteIcon) {
    for (let i = 0; i < Math.round(beats); i++) {
      slashes.push('quarter');
      if (eighthNotes) slashes.push('eighth');
    }
  }

  return (
    <div style={{
      flex: flexValue, display: 'flex', flexDirection: 'column',
      minWidth: 0, overflow: 'hidden',
      borderRight: !isLast ? '1px solid var(--color-rule)' : undefined,
      paddingRight: !isLast ? 2 : 0, marginRight: !isLast ? 2 : 0,
      backgroundColor: isChordPlaying ? 'rgba(99,102,241,0.18)' : undefined,
      borderRadius: isChordPlaying ? 4 : undefined,
      outline: isChordPlaying ? '2px solid rgba(99,102,241,0.7)' : undefined,
      transition: 'background-color 0.05s, outline 0.05s',
    }}>
      <div style={{
        minHeight: Math.max(28, fontSize * 1.1), display: 'flex',
        flexDirection: 'column', alignItems: 'flex-start',
        justifyContent: 'flex-end', paddingBottom: 2, paddingLeft: 4, overflow: 'hidden',
      }}>
        {chord && (
          <>
            <span style={{ fontSize, lineHeight: 1, display: 'inline-flex', alignItems: 'baseline' }}>
              <ChordSymbol chord={chord} mode={displayMode} sectionKey={sectionKey} compact={false} overrideFontSize={fontSize} />
            </span>
            {analysisSettings?.enabled && (
              <div style={{ marginTop: 2 }}>
                <ChordAnalysisLabel chord={chord} sectionKey={sectionKey} settings={analysisSettings} showBackground />
              </div>
            )}
          </>
        )}
      </div>

      {showNoteIcon && noteValue ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingBottom: 6, paddingTop: 2, color: 'var(--color-ink-primary)', opacity: 0.7 }}>
          <NoteIcon nv={noteValue} size={iconSize} />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingLeft: 2 }}>
          {slashes.map((type, i) =>
            type === 'quarter' ? <QuarterSlash key={i} /> : <EighthSlash key={i} />
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// PROPS
// ------------------------------------------------------------

interface SlashBarProps {
  bar: Bar;
  section: Section;
  sectionIndex: number;
  barIndex: number;
  isSelected: boolean;
  isRangeSelected?: boolean;
  onSelect: () => void;
  onShiftClick?: () => void;
  // 'nextSection' added for Tab navigation
  onNavigate: (direction: 'prev' | 'next' | 'up' | 'down' | 'nextSection') => void;
  onDeleteBar: () => void;
}

// ------------------------------------------------------------
// COMPONENT
// ------------------------------------------------------------

export function SlashBar({
  bar, section, sectionIndex, barIndex,
  isSelected, isRangeSelected = false,
  onSelect, onShiftClick, onNavigate, onDeleteBar,
}: SlashBarProps) {
  const song              = useSongStore((s) => s.song);
  const setBarInput       = useSongStore((s) => s.setBarInput);
  const toggleEighthNotes = useSongStore((s) => s.toggleEighthNotes);
  const setBarNote        = useSongStore((s) => s.setBarNote);
  const setBarLyrics      = useSongStore((s) => s.setBarLyrics);
  const setBarRhythm      = useSongStore((s) => s.setBarRhythm);
  const removeRepeat      = useSongStore((s) => s.removeRepeat);
  const setRepeat         = useSongStore((s) => s.setRepeat);

  const sectionKey  = effectiveKey(song, section);
  const ts          = effectiveTimeSignature(song, bar);
  const displayMode = song.displayMode;
  const totalBeats  = ts.numerator;

  const containerRef = useRef<HTMLDivElement>(null);
  const barWidthPx   = useBarWidth(containerRef);

  const [isEditing, setIsEditing]                 = useState(false);
  const [inputValue, setInputValue]               = useState('');
  const [parseErrors, setParseErrors]             = useState<string[]>([]);
  const [showRepeatPopover, setShowRepeatPopover] = useState(false);
  const [showRhythmPanel, setShowRhythmPanel]     = useState(false);
  const [hovered, setHovered]                     = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Playback — chord-level cursor
  const isBarPlaying = useIsBarPlaying(sectionIndex, barIndex);
  const cursor       = usePlaybackCursor();

  useEffect(() => {
    if (isSelected && !isEditing) {
      setInputValue(chordsToInputString(bar.chords, totalBeats));
      setParseErrors([]);
      setIsEditing(true);
    }
    if (!isSelected && isEditing) {
      setIsEditing(false);
      setParseErrors([]);
    }
  }, [isSelected]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    if (value.trim() === '') { setBarInput(section.id, bar.id, ''); setParseErrors([]); return; }
    const result = setBarInput(section.id, bar.id, value);
    if (!result.ok && result.errors) {
      setParseErrors(result.errors.map((e) => `'${e.token}': ${e.error}`));
    } else {
      setParseErrors([]);
    }
  }, [section.id, bar.id, setBarInput]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Tab':
        e.preventDefault();
        onNavigate(e.shiftKey ? 'prev' : 'nextSection');
        break;
      case 'Enter':
        e.preventDefault();
        onNavigate(e.shiftKey ? 'prev' : 'next');
        break;
      case 'Escape':
        e.preventDefault();
        setInputValue(chordsToInputString(bar.chords, totalBeats));
        setParseErrors([]);
        onSelect();
        break;
      case 'ArrowLeft':
        if (inputRef.current?.selectionStart === 0) { e.preventDefault(); onNavigate('prev'); }
        break;
      case 'ArrowRight':
        if (inputRef.current?.selectionStart === inputRef.current?.value.length) { e.preventDefault(); onNavigate('next'); }
        break;
      case 'ArrowUp':   e.preventDefault(); onNavigate('up');   break;
      case 'ArrowDown': e.preventDefault(); onNavigate('down'); break;
      case 'Backspace':
        if (inputValue === '') { e.preventDefault(); onDeleteBar(); }
        break;
    }
  }, [bar, inputValue, totalBeats, onNavigate, onSelect, onDeleteBar]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    setParseErrors([]);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (e.shiftKey) { e.preventDefault(); onShiftClick?.(); return; }
    if (!isSelected) onSelect();
  }, [isSelected, onSelect, onShiftClick]);

  const handleRepeatCountChange = useCallback((newCount: number) => {
    const bars   = section.bars;
    const idx    = bars.findIndex((b) => b.id === bar.id);
    let startIdx = idx;
    while (startIdx > 0 && !bars[startIdx].repeatStart) startIdx--;
    if (bars[startIdx].repeatStart) setRepeat(section.id, bars[startIdx].id, bar.id, newCount);
  }, [section, bar.id, setRepeat]);

  const handleRhythmConfirm = useCallback((slots: RhythmSlot[]) => {
    const positions = slotsToPositions(slots);
    setBarRhythm(section.id, bar.id, positions);
    setShowRhythmPanel(false);
  }, [section.id, bar.id, setBarRhythm]);

  const initialSlots  = chordsToRhythmSlots(bar, totalBeats);
  const showNoteIcons = bar.hasCustomRhythm && bar.chords.length > 0;

  interface Segment { chord: Chord | null; beats: number; }

  const segments: Segment[] = useMemo(() => {
    if (bar.chords.length === 0) return [{ chord: null, beats: totalBeats }];
    return bar.chords.map((chord, i) => {
      const nextPos = bar.chords[i + 1]?.position ?? totalBeats;
      const beats   = Math.max(0.25, nextPos - chord.position);
      return { chord: chord.isEmpty ? null : chord, beats };
    });
  }, [bar.chords, totalBeats]);

  const hasRepeatStart = bar.repeatStart;
  const hasRepeatEnd   = bar.repeatEnd !== null;
  const hasError       = parseErrors.length > 0;
  const hasLyrics      = bar.lyrics !== null && bar.lyrics !== '';

  return (
    <>
      <div
        ref={containerRef}
        className={[
          'relative border border-rule transition-colors cursor-pointer',
          isSelected
            ? hasError ? 'bg-selection border-error' : 'bg-selection border-accent'
            : isRangeSelected ? 'bg-selection/50'
            : isBarPlaying ? 'bg-accent/10 border-accent/40'
            : hovered ? 'bg-hover' : '',
        ].filter(Boolean).join(' ')}
        style={{ minHeight: 80 }}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <BarTimeSigControl
          sectionId={section.id} barId={bar.id}
          barTimeSignature={bar.timeSignature} effectiveTs={ts}
          visible={hovered || bar.timeSignature !== null}
        />
        <BarOptionsButton bar={bar} section={section} showEighthNotes />

        {hasRepeatStart && <RepeatStartBarline />}
        {hasRepeatEnd && (
          <>
            <RepeatCountBadge count={bar.repeatEnd!} />
            <RepeatEndBarline count={bar.repeatEnd!} onClick={(e) => { e.stopPropagation(); setShowRepeatPopover((v) => !v); }} />
            {showRepeatPopover && (
              <RepeatEndPopover
                count={bar.repeatEnd!}
                onCountChange={handleRepeatCountChange}
                onRemove={() => { removeRepeat(section.id, bar.id); setShowRepeatPopover(false); }}
                onClose={() => setShowRepeatPopover(false)}
              />
            )}
          </>
        )}

        {isEditing ? (
          <div style={{ padding: '8px 10px', paddingLeft: hasRepeatStart ? 18 : undefined, paddingRight: hasRepeatEnd ? 28 : undefined }}>
            <div style={{ display: 'flex', flexDirection: 'row', opacity: 0.15, marginBottom: 4, pointerEvents: 'none' }}>
              {segments.map((seg, i) => (
                <ChordSegment key={i} chord={null} beats={seg.beats} totalBeats={totalBeats} barWidthPx={barWidthPx} eighthNotes={bar.eighthNotes} showNoteIcon={false} sectionKey={sectionKey} displayMode={displayMode} isLast={i === segments.length - 1} isChordPlaying={false} />
              ))}
            </div>
            <input
              ref={inputRef} type="text" value={inputValue}
              onChange={handleChange} onKeyDown={handleKeyDown} onBlur={handleBlur}
              className="bar-input w-full text-center bg-transparent outline-none font-mono text-base text-ink-primary placeholder-ink-muted caret-accent"
              placeholder="1 5 6m 4" spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off"
            />
            {hasError && <div className="text-[10px] text-error font-mono mt-1 truncate">{parseErrors[0]}</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', width: '100%', padding: '6px 8px 8px', boxSizing: 'border-box', paddingLeft: hasRepeatStart ? 18 : undefined, paddingRight: hasRepeatEnd ? 28 : undefined }}>
            {segments.map((seg, i) => (
              <ChordSegment
                key={i}
                chord={seg.chord}
                beats={seg.beats}
                totalBeats={totalBeats}
                barWidthPx={barWidthPx}
                eighthNotes={bar.eighthNotes}
                showNoteIcon={showNoteIcons}
                sectionKey={sectionKey}
                displayMode={displayMode}
                isLast={i === segments.length - 1}
                analysisSettings={song.analysisSettings}
                isChordPlaying={
                  cursor !== null &&
                  cursor.sectionIdx === sectionIndex &&
                  cursor.barIdx === barIndex &&
                  cursor.chordIdx === i
                }
              />
            ))}
          </div>
        )}

        {(bar.note || hovered) && !isEditing && (
          <div style={{ padding: '0 8px 4px', paddingLeft: 38 }} className="print:pl-2" onClick={(e) => e.stopPropagation()}>
            <AnnotationField value={bar.note ?? null} onChange={(note) => setBarNote(section.id, bar.id, note)} placeholder="Add note…" showHint={hovered && !bar.note} />
          </div>
        )}

        {(hasLyrics || hovered) && !isEditing && (
          <div style={{ padding: '0 8px 6px', paddingLeft: hasRepeatStart ? 18 : 8 }} className="print:pl-2" onClick={(e) => e.stopPropagation()}>
            <LyricsField value={bar.lyrics ?? null} onChange={(lyrics) => setBarLyrics(section.id, bar.id, lyrics)} showHint={hovered && !hasLyrics} onTabNext={() => onNavigate('next')} onTabPrev={() => onNavigate('prev')} />
          </div>
        )}

        {hovered && !isEditing && (
          <button onClick={(e) => { e.stopPropagation(); toggleEighthNotes(section.id, bar.id); }} title={bar.eighthNotes ? 'Remove 8th notes' : 'Add 8th notes'}
            style={{ position: 'absolute', bottom: 4, right: hasRepeatEnd ? 30 : 6, fontSize: 9, fontFamily: 'var(--font-mono)', color: bar.eighthNotes ? 'var(--color-accent)' : 'var(--color-ink-muted)', opacity: 0.85, background: 'transparent', border: 'none', cursor: 'pointer', letterSpacing: '0.04em', padding: '2px 4px', lineHeight: 1 }}>
            {bar.eighthNotes ? '♪×8' : '♪×4'}
          </button>
        )}

        {hovered && !isEditing && (
          <button onClick={(e) => { e.stopPropagation(); setShowRhythmPanel(true); }} title="Edit rhythm"
            style={{ position: 'absolute', top: 4, right: hasRepeatEnd ? 52 : 28, fontSize: 11, fontFamily: 'var(--font-mono)', color: showNoteIcons ? 'var(--color-accent)' : 'var(--color-ink-muted)', opacity: 0.85, background: 'transparent', border: 'none', cursor: 'pointer', letterSpacing: '0.04em', padding: '2px 4px', lineHeight: 1 }}>
            ♩
          </button>
        )}
      </div>

      {showRhythmPanel && (
        <RhythmInputPanel timeSignature={ts} initialSlots={initialSlots} onConfirm={handleRhythmConfirm} onClose={() => setShowRhythmPanel(false)} />
      )}
    </>
  );
}